import { BDI3ParsedResult } from "@api/assessments/assessment-schema-types";
import type { AssessmentLayoutConfig } from "@components/AssessmentFormRenderer/types/layoutTypes";

export const BDI3_LAYOUT: AssessmentLayoutConfig = {
  parsedResultSchema: BDI3ParsedResult,
  sections: [
    // --- Intake / header metadata ---
    { type: "text_field", key: "Name", label: "Name", editable: true },
    {
      type: "text_field",
      key: "Date of Testing",
      label: "Date of Testing",
      editable: true,
      validation: { inputType: "date" },
    },
    { type: "text_field", key: "Chronological Age", label: "Chronological Age", editable: true },
    { type: "text_field", key: "Gender", label: "Gender", editable: true },
    { type: "text_field", key: "Location", label: "Location", editable: true },
    {
      type: "text_field",
      key: "Date of Birth",
      label: "Date of Birth",
      editable: true,
      validation: { inputType: "date" },
    },
    { type: "text_field", key: "Label", label: "Label", editable: true },

    // --- Page 1: Domain(s) Administered ---
    {
      type: "score_table",
      label: "Domain(s) Administered",
      source: "Domain(s) Administered",
      columns: [
        { key: "Domain", label: "Domain", editable: false },
        { key: "Examiner", label: "Examiner", editable: true },
        {
          key: "Date of Testing",
          label: "Date of Testing",
          editable: true,
          validation: { inputType: "date" },
        },
      ],
    },

    // --- Page 2: Table of Scores ---
    // `step: 0.1` on every numeric column: Standard Deviation is always one of
    // -2.0/-1.5/-1.0 per the form's own legend, and without an explicit step the
    // native number input defaults to step="1", which flags any decimal value as
    // a native stepMismatch (shown as invalid/red) even though the Zod schema
    // and backend both accept any number.
    {
      type: "score_table",
      label: "Table of Scores",
      source: "Table of Scores",
      columns: [
        { key: "Domain", label: "Domain", editable: false },
        {
          key: "Raw Score",
          label: "Raw Score",
          editable: true,
          validation: { inputType: "number", step: 0.1 },
        },
        {
          key: "Standard Deviation",
          label: "Standard Deviation",
          editable: true,
          validation: { inputType: "number", step: 0.1 },
        },
        {
          key: "Cut Score",
          label: "Cut Score",
          editable: true,
          validation: { inputType: "number", step: 0.1 },
        },
        {
          key: "Pass/Refer",
          label: "Pass/Refer",
          editable: true,
          options: ["PASS", "REFER"],
        },
      ],
    },

    // --- Page 2: Total Screening Score ---
    // A single optional object (null unless >1 domain was administered), not a list
    // row — there's no established "optional summary row" section type in this
    // repo (score_table/dynamic_score_tables/key_value_list all expect an array
    // source). The closest existing precedent is ADI-R Toddler's "Header.Date of
    // ADI-R Administration" pattern: a text_field `key` can dot-path into a
    // nested object. That mechanism degrades gracefully when the parent object
    // is null (fields just render blank), which is exactly the single-domain case.
    {
      type: "text_field",
      key: "Total Screening Score.Raw Score",
      label: "Total Screening Score — Raw Score",
      editable: true,
      validation: { inputType: "number", step: 0.1 },
    },
    {
      type: "text_field",
      key: "Total Screening Score.Standard Deviation",
      label: "Total Screening Score — Standard Deviation",
      editable: true,
      validation: { inputType: "number", step: 0.1 },
    },
    {
      type: "text_field",
      key: "Total Screening Score.Cut Score",
      label: "Total Screening Score — Cut Score",
      editable: true,
      validation: { inputType: "number", step: 0.1 },
    },
    {
      type: "single_select",
      key: "Total Screening Score.Pass/Refer",
      label: "Total Screening Score — Pass/Refer",
      editable: true,
      options: [
        { label: "PASS", value: "PASS" },
        { label: "REFER", value: "REFER" },
      ],
    },
    {
      type: "text_field",
      key: "Total Screening Score.Age Equivalent",
      label: "Total Screening Score — Age Equivalent",
      editable: true,
    },

    // --- Page 4: Item Level Scores ---
    // NOT modeled as `grouped_rows` despite the superficial "array grouped by a
    // key" shape match. FormGroupedRows requires a real per-row `rowLayoutKey`
    // field whose string value encodes which columns to render for that row
    // (e.g. ADI-R Toddler's genuine `row_layout: "code_score" | "value"` field) —
    // it exists to support algorithm tables with genuinely variable columns per
    // row. BDI-3 item rows always have the same two columns (SKILL, MASTERY);
    // there is no such discriminator field, and none of DOMAIN/SKILL/MASTERY's
    // own values ever equal the literal column key needed ("SKILL"/"MASTERY"),
    // so wiring grouped_rows here would silently fail to bind those columns.
    // `score_table`'s definition — "Fixed-column table, array of row objects" —
    // is the exact match for this shape. Domain is kept as a plain column
    // (rather than a nested heading) since the array already arrives grouped by
    // domain in row order (see BDI3ItemScore docstring), so a flat table still
    // reads as domain-clustered. Keys are UPPERCASE (DOMAIN/SKILL/MASTERY) to
    // match the literal form headers, per the FDM schema.
    {
      type: "score_table",
      label: "Item Level Scores",
      source: "Item Level Scores",
      columns: [
        { key: "DOMAIN", label: "Domain", editable: false },
        { key: "SKILL", label: "Skill", editable: false },
        {
          key: "MASTERY",
          label: "Mastery",
          editable: true,
          options: ["MASTERED", "EMERGING", "FUTURE LEARNING OBJECTIVE"],
        },
      ],
    },
  ],
};
